import React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { resetPasswordRequestThunk } from "reduxStore/auth/thunks";
import { useAppSelector } from "hooks/useAppSelector";
import { authStatusSelector } from "reduxStore/auth/selectors";
import { getValidationSchema, initialValues } from "./config";
import { IResetPasswordRequestCredentials } from "types/auth";
import { useAppDispatch } from "hooks/useAppDispatch";
import { StyledFormicError } from "modals/ResetPasswordModal/styles";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { Messages, NotificationSeverity } from "helpers/constants";
import { AuthContainer } from "components/Layout/AuthContainer";
import { useNavigate } from "react-router-dom";
import { GuideLink } from "pages/AuthGuides/GuideLink";
import { AdminRoutes } from "config/routes";

const ResetPasswordRequestWrapper: React.FC = () => {
  const statuses = useAppSelector(authStatusSelector);
  const error = statuses["auth/resetPasswordRequestThunk"]?.error;
  const loading = statuses["auth/resetPasswordRequestThunk"]?.pending;
  const success = statuses["auth/resetPasswordRequestThunk"]?.success;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleSubmit = (values: IResetPasswordRequestCredentials) => {
    dispatch(resetPasswordRequestThunk(values));
    if (success) {
      dispatch(
        showNotificationThunk({
          message: Messages.successResetPasswordRequest,
          severity: NotificationSeverity.success,
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema,
    onSubmit: (values) => {
      const data = {
        email: values.email?.toString()?.trim() || "",
      };
      handleSubmit(data);
    },
  });

  return (
    <AuthContainer
      loading={loading}
      loadingText="Sending reset password request..."
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Typography component="h1" variant="h6">
          Reset Password
        </Typography>
        <FormikProvider value={formik}>
          <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
            />
            <ErrorMessage name="email" component={StyledFormicError} />
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                disabled={loading}
                type="submit"
                fullWidth
                color="primary"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Submit
              </Button>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            {error && (
              <StyledFormicError>
                Invalid Email, please check and try again.
              </StyledFormicError>
            )}
            <GuideLink
              to={AdminRoutes.ResetPasswordGuide}
              label="View step-by-step guide"
            />
            <Button
              disabled={loading}
              fullWidth
              variant="text"
              color="primary"
              sx={{ mt: 1, mb: 2 }}
              onClick={() => navigate("/")}
            >
              Go to Login
            </Button>
          </Box>
        </FormikProvider>
      </Box>
    </AuthContainer>
  );
};

export default ResetPasswordRequestWrapper;
